import { AbstractControl, AsyncValidatorFn, FormControl, ValidatorFn } from '@angular/forms';
import * as dayjs from 'dayjs';
import { CountryCode } from 'libphonenumber-js';

import { toE164 } from './utils';

export interface ValidationResult {
  [key: string]: boolean;
}
export class CustomValidators {
  static onlyChar(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (control.value === '') return null;

      const re = /^[a-zA-Z ]*$/;
      if (re.test(control.value)) {
        return null;
      }
      return { onlyChar: true };
    };
  }

  static mustMatch(controlName: string, checkControlName: string): ValidatorFn {
    return (controls: AbstractControl) => {
      const control = controls.get(controlName);
      const checkControl = controls.get(checkControlName);

      if (checkControl?.errors && !checkControl.errors['matching']) {
        return null;
      }

      if (control?.value !== checkControl?.value) {
        controls.get(checkControlName)?.setErrors({ matching: true });
        return { matching: true };
      }
      if (checkControl.errors && checkControl.errors['matching']) {
        if (Object.keys(checkControl.errors).length > 1) delete checkControl.errors['matching'];
        else checkControl.setErrors(null);
      }
      if (control.errors && control.errors['matching']) {
        delete control.errors['matching'];
        if (Object.keys(control.errors).length > 1) delete control.errors['matching'];
        else control.setErrors(null);
      }
      return null;
    };
  }

  static requiredFileType(type: string) {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (control.value === '') return null;
      const file = control.value;
      if (file) {
        const extension = file.split('.')[1].toLowerCase();
        if (type.toLowerCase() !== extension.toLowerCase()) {
          return {
            requiredFileType: true,
          };
        }
        return null;
      }
      return null;
    };
  }

  static strong(control: FormControl): ValidationResult {
    const hasNumber = /\d/.test(control.value);
    const hasUpper = /[A-Z]/.test(control.value);
    const hasLower = /[a-z]/.test(control.value);
    const hasSymbol = /[!@#$%^&*()_+={}\\[\]:;,.<>?/-]/.test(control.value);

    const valid = hasNumber && hasUpper && hasLower && hasSymbol;
    if (!valid) {
      return { strong: true };
    }
    return null;
  }

  static mustBePositive(controlNames: string[]): ValidatorFn {
    return (controls: AbstractControl) => {
      let sum = 0;
      controlNames.forEach((controlName) => {
        sum += controls.get(controlName).value;
      });
      if (sum > 0) {
        return null;
      }
      return { mustBePositive: true };
    };
  }

  static futureDate(control: AbstractControl): ValidationResult {
    const date = new Date(control.value);
    const today = new Date();
    if (date > today) {
      return null;
    }
    return { futureDate: true };
  }

  static pastDate(control: AbstractControl): ValidationResult {
    const date = new Date(control.value);
    const today = new Date();
    if (date < today) {
      return null;
    }
    return { pastDate: true };
  }

  static checkAnimalAge(value: string | Date): boolean {
    const birthDate = dayjs(value);
    const today = dayjs();

    if (!birthDate.isValid() || birthDate.isAfter(today)) {
      return false;
    }

    const ageInMonths = today.diff(birthDate, 'month');
    const ageInYears = today.diff(birthDate, 'year');

    if (ageInMonths >= 3 && ageInYears <= 6) {
      return true;
    }

    return false;
  }

  static animalAgeValidator(control: AbstractControl): ValidationResult | null {
    if (!CustomValidators.checkAnimalAge(control.value)) {
      return { invalidAge: true };
    }
    return null;
  }

  static minimalDateValidator(control: AbstractControl): ValidationResult | null {
    const rawDate = new Date(control.value);
    const rawYear = rawDate.getFullYear();
    if (rawYear < 1900) {
      return { invalidYear: true };
    }

    const value = dayjs(control.value);
    if (!value.isValid()) {
      return { invalidDate: true };
    }
    return null;
  }

  static adultAgeValidator(control: AbstractControl): ValidationResult {
    const value = dayjs(control.value);

    if (!value.isValid()) {
      return { invalidDate: true };
    }

    const adultDate = dayjs().subtract(18, 'year');
    if (value.isAfter(adultDate)) {
      return { invalidAge: true };
    }

    return null;
  }

  static childAgeValidator(control: AbstractControl): ValidationResult {
    const value = new Date(control.value);
    const date = new Date();
    date.setFullYear(date.getFullYear() - 18);
    if (value > date) {
      return null;
    }
    return { notChild: true };
  }

  static prenatalAgeValidator(control: AbstractControl): ValidationResult {
    const value = new Date(control.value);
    const today = new Date();
    const sixMonthsFromToday = new Date();
    sixMonthsFromToday.setMonth(today.getMonth() + 6);
    if (value >= today && value <= sixMonthsFromToday) {
      return null;
    }
    return { notPrenatal: true };
  }

  static addressValidator(orderedAddressControl: string[]): AsyncValidatorFn {
    return async (controls: AbstractControl) => {
      const geocoder = new google.maps.Geocoder();

      if (orderedAddressControl.some((controlName) => !controls.get(controlName).value)) {
        return null;
      }

      const formattedAddress = orderedAddressControl
        .map((controlName) => controls.get(controlName).value)
        .join(', ');
      try {
        const addressResults = await geocoder.geocode(
          { address: formattedAddress },
          (results) => results,
        );

        if (addressResults.results.length === 0) {
          controls.get(orderedAddressControl[0])?.setErrors({ invalidAddress: true });
          return { invalidAddress: true };
        }
        return null;
      } catch (e) {
        return { invalidAddress: true };
      }
    };
  }

  /** Rejects anything that is not a dialable number. Leaves empty values to `required`. */
  static phoneNumber(defaultCountry: CountryCode = 'CH'): ValidatorFn {
    return (control: AbstractControl): ValidationResult | null => {
      if (!control.value) return null;
      return toE164(control.value, defaultCountry) ? null : { phoneNumber: true };
    };
  }

  static npaValidator = (control: AbstractControl): ValidationResult => {
    const npa = control.value;
    if (npa && !/^\d{4}$/.test(npa)) {
      return { npa: true };
    }
    return null;
  };

  static selectedVehicleValidator: ValidatorFn = (control: AbstractControl): ValidationResult => {
    const selectedVehicle = control.value;

    if (!selectedVehicle || !selectedVehicle.brand || !selectedVehicle.model) {
      return { selectedVehicle: true };
    }
    return null;
  };

  static arrayValidator(control: AbstractControl): ValidationResult {
    if (!Array.isArray(control.value)) return { notArray: true };
    return null;
  }

  static notAfterDate(maxDate: Date | string): ValidatorFn {
    return (control: AbstractControl): ValidationResult | null => {
      if (!control.value) return null;

      const inputDate = dayjs(control.value);
      const referenceDate = dayjs(maxDate);

      if (!inputDate.isValid()) {
        return { invalidDate: true };
      }

      if (!referenceDate.isValid()) {
        return { invalidReferenceDate: true };
      }

      if (inputDate.isAfter(referenceDate, 'day')) {
        return { notAfterDate: true };
      }

      return null;
    };
  }

  static afterDate(maxDate: Date | string): ValidatorFn {
    return (control: AbstractControl): ValidationResult | null => {
      if (!control.value) return null;

      const inputDate = dayjs(control.value);
      const referenceDate = dayjs(maxDate);

      if (!inputDate.isValid()) {
        return { invalidDate: true };
      }

      if (!referenceDate.isValid()) {
        return { invalidReferenceDate: true };
      }

      if (inputDate.isBefore(referenceDate, 'day')) {
        return { afterDate: true };
      }

      return null;
    };
  }
}
